/**
 * @description
 * React context for authentication state management. Provides authentication
 * status, user information, and methods for login/logout across the application.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 * @see /admin/src/lib/auth.ts
 */

import React, { createContext, useEffect, useState, useCallback } from 'react';
import type { User, OAuthProvider, AuthContextValue } from '../../types/auth';
import { AuthError } from '../../types/auth';
import {
  getCurrentUser,
  logout as authLogout,
} from '../../lib/auth';
import { AuthErrorBoundary } from '../components/auth/AuthErrorBoundary';
import { setSentryUser, clearSentryUser, addSentryBreadcrumb } from '../lib/sentry';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Export the context for use in hooks
export { AuthContext };

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication provider component
 */
function AuthProviderInner({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  /**
   * Load current user from stored token
   */
  const loadUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const currentUser = await getCurrentUser();
      setUser(currentUser);
      
      // Set user context in Sentry
      if (currentUser) {
        setSentryUser({
          id: currentUser.id,
          email: currentUser.email,
          name: currentUser.name,
          role: currentUser.role,
        });
        addSentryBreadcrumb('User authenticated', 'auth', {
          userId: currentUser.id,
          email: currentUser.email,
        });
      }
    } catch (err) {
      setUser(null);
      clearSentryUser();
      
      if (err instanceof AuthError) {
        setError(err);
        addSentryBreadcrumb('Authentication failed', 'auth', {
          error: err.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load user session on mount
   */
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * Initiate OAuth login flow - simple redirect to backend
   */
  const login = useCallback(async (provider: OAuthProvider) => {
    // Simple redirect to backend OAuth - no state management needed
    window.location.href = `/auth/login/${provider}`;
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    authLogout();
    setUser(null);
    
    // Clear user context in Sentry
    clearSentryUser();
    addSentryBreadcrumb('User logged out', 'auth');
    
    // Redirect to home page
    window.location.href = '/';
  }, []);

  /**
   * Refresh user session
   */
  const refreshSession = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    refreshSession,
  };

  if (error && !isLoading) {
    throw error;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * AuthProvider with Error Boundary
 */
export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <AuthErrorBoundary>
      <AuthProviderInner>{children}</AuthProviderInner>
    </AuthErrorBoundary>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
